import { Button, Form, Input, Modal, Space, Typography } from 'antd';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GlobalContex from '../../../Store/GlobalContext';
import useOtp from '../../../hooks/useOtp';
import useMessageUser from '../../../hooks/useMessageUser';
import useSetLanguage from '../../../hooks/useSetLanguage';
import { AUTH_TOKEN, REFRESH_TOKEN, USER_INFO } from '../../../config/config';

type Step = 'credentials' | 'qr-setup' | 'otp-login';

const { Text } = Typography;

function UserLoginForm() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('credentials');
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [form] = Form.useForm();

  const { t } = useTranslation();
  const { loginUser, setupOtp, verifySetup, loginWithOtp, loading } = useOtp();
  const { showMessage } = useMessageUser();
  const { getLanguage } = useSetLanguage();
  const { user: { setAuth } } = useContext(GlobalContex);
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
    setStep('credentials');
    setCredentials(null);
    setQrCodeUrl(null);
    form.resetFields();
  };

  const onSubmitCredentials = async (values: { email: string; password: string }) => {
    try {
      const loginResult = await loginUser(values.email, values.password);
      setCredentials({ email: values.email, password: values.password });
      form.resetFields();

      if (!loginResult.otpConfigured) {
        const setupResult = await setupOtp(values.email, values.password);
        setQrCodeUrl(setupResult.qrCodeUrl);
        setStep('qr-setup');
      } else {
        setStep('otp-login');
      }
    } catch (error: any) {
      showMessage(error?.message || t('loginError'), 'error');
    }
  };

  const onVerifyQrSetup = async (values: { otpCode: string }) => {
    if (!credentials) return;
    try {
      await verifySetup(credentials.email, values.otpCode);
      form.resetFields();
      setStep('otp-login');
    } catch (error: any) {
      showMessage(error?.message || t('otpInvalid'), 'error');
    }
  };

  const onSubmitOtpLogin = async (values: { otpCode: string }) => {
    if (!credentials) return;
    try {
      const result = await loginWithOtp(credentials.email, credentials.password, values.otpCode);
      const roles = result.roles || ['ROLE_USER'];

      localStorage.setItem(AUTH_TOKEN, result.token);
      localStorage.setItem(REFRESH_TOKEN, result.refreshToken);
      localStorage.setItem(USER_INFO, JSON.stringify({ user: result.data, roles }));
      setAuth({ user: result.data, roles });
      getLanguage(result.data?.language?.lanName);

      handleClose();

      if (roles[0] === 'ROLE_SYSADMIN') {
        navigate('/users');
      } else if (roles[0] === 'ROLE_TRADUCTOR') {
        navigate('/scuapp/traductor');
      } else {
        navigate('/home');
      }
    } catch (error: any) {
      showMessage(error?.message || t('otpInvalid'), 'error');
    }
  };

  const modalTitles: Record<Step, string> = {
    credentials: t('loginTitle'),
    'qr-setup': t('qrSetupTitle'),
    'otp-login': t('otpLoginTitle'),
  };

  const renderStep = () => {
    switch (step) {
      case 'credentials':
        return (
          <Form form={form} layout="vertical" onFinish={onSubmitCredentials} requiredMark={false}>
            <Form.Item
              label={t('loginEmail')}
              name="email"
              rules={[
                { required: true, message: t('loginEmailRequired') },
                { type: 'email', message: t('loginEmailInvalid') },
              ]}
            >
              <Input placeholder={t('loginEmailPlaceholder')} autoComplete="email" />
            </Form.Item>
            <Form.Item
              label={t('loginPassword')}
              name="password"
              rules={[{ required: true, message: t('loginPasswordRequired') }]}
            >
              <Input.Password placeholder="********" autoComplete="current-password" />
            </Form.Item>
          </Form>
        );

      case 'qr-setup':
        return (
          <Form form={form} layout="vertical" onFinish={onVerifyQrSetup} requiredMark={false}>
            <Space direction="vertical" align="center" style={{ width: '100%', marginBottom: 16 }}>
              <Text>{t('qrSetupScanText')}</Text>
              {qrCodeUrl && (
                <img
                  src={qrCodeUrl}
                  alt={t('qrSetupAlt')}
                  style={{ width: 200, height: 200 }}
                />
              )}
              <Text type="secondary">{t('qrSetupHint')}</Text>
            </Space>
            <Form.Item
              label={t('otpCodeLabel')}
              name="otpCode"
              rules={[
                { required: true, message: t('otpCodeRequired') },
                { len: 6, message: t('otpCodeLength') },
              ]}
            >
              <Input placeholder="123456" maxLength={6} autoComplete="one-time-code" />
            </Form.Item>
          </Form>
        );

      case 'otp-login':
        return (
          <Form form={form} layout="vertical" onFinish={onSubmitOtpLogin} requiredMark={false}>
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
              <Text>{t('otpLoginHint')}</Text>
            </Space>
            <Form.Item
              label={t('otpCodeLabel')}
              name="otpCode"
              rules={[
                { required: true, message: t('otpCodeRequired') },
                { len: 6, message: t('otpCodeLength') },
              ]}
            >
              <Input placeholder="123456" maxLength={6} autoComplete="one-time-code" />
            </Form.Item>
          </Form>
        );
    }
  };

  return (
    <div>
      <Button type="primary" block onClick={() => setOpen(true)} ghost>
        {t('loginGuestBtn')}
      </Button>

      <Modal
        open={open}
        title={modalTitles[step]}
        onCancel={handleClose}
        onOk={() => form.submit()}
        okText={t('btnContinue')}
        cancelText={t('btnCancel')}
        confirmLoading={loading}
      >
        {renderStep()}
      </Modal>
    </div>
  );
}

export default UserLoginForm;
